import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Field } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const TZ_CC = { "Europe/London": "44", "Europe/Rome": "39", "Europe/Dublin": "353", "Europe/Paris": "33", "Europe/Madrid": "34",
  "Europe/Berlin": "49", "Europe/Lisbon": "351", "Europe/Amsterdam": "31", "Europe/Zurich": "41", "Europe/Brussels": "32" };

export const waLink = (phone, text = "", timezone = "") => {
  const raw = (phone || "").trim();
  let d = raw.replace(/\D/g, "");
  const cc = TZ_CC[timezone];
  if (d.startsWith("00")) d = d.slice(2);
  else if (!raw.startsWith("+") && cc) {
    if (cc === "39") { if (!d.startsWith("39") || d.length <= 10) d = `39${d}`; }
    else if (d.startsWith("0")) d = `${cc}${d.slice(1)}`;
  }
  return `https://wa.me/${d}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
};

export function ContactButtons({ contact, testPrefix = "contact" }) {
  if (!contact?.whatsapp && !contact?.email) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-buttons`}>
      {contact.whatsapp && <a href={waLink(contact.whatsapp)} target="_blank" rel="noreferrer" data-testid={`${testPrefix}-whatsapp`}>
        <Button variant="outline" className="rounded-full border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp us</Button></a>}
      {contact.email && <a href={`mailto:${contact.email}`} data-testid={`${testPrefix}-email`}>
        <Button variant="outline" className="rounded-full"><Mail className="h-4 w-4 mr-1.5" />Email us</Button></a>}
    </div>
  );
}

export function WhatsAppFloat({ contact }) {
  if (!contact?.whatsapp) return null;
  return (
    <a href={waLink(contact.whatsapp)} target="_blank" rel="noreferrer" aria-label="Message us on WhatsApp" data-testid="whatsapp-float"
      className="fixed bottom-5 left-5 z-40 h-12 w-12 rounded-full bg-[#25D366] text-white grid place-items-center shadow-lg transition-transform duration-200 hover:scale-110">
      <MessageCircle className="h-6 w-6" />
    </a>
  );
}

export function ContactSettings() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/settings/contact").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = () => api.put("/settings/contact", c).then((r) => { setC(r.data); toast.success("Saved"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="max-w-xl rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="contact-settings">
      <p className="text-sm text-muted-foreground">Patients use these to message you: a WhatsApp button on your public pages, booking confirmations and the patient portal. Replies to emails you send from the app go to this email address.</p>
      <Field label="WhatsApp Business number (with country code)"><Input data-testid="contact-whatsapp-input" placeholder="+44 7700 900000" value={c.whatsapp} onChange={(e) => setC({ ...c, whatsapp: e.target.value })} /></Field>
      <Field label="Clinic email for patient replies"><Input type="email" data-testid="contact-email-input" placeholder="hello@yourclinic.com" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></Field>
      <Button className="rounded-full" data-testid="contact-save-btn" onClick={save}>Save</Button>
    </div>
  );
}
